import { useQuery } from "@tanstack/react-query";
import { otApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsOtI,
  OtI,
  ObjPostI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getOts = async ({
  pagination,
  sorting,
  globalFilter,
  client,
  supplier,
  equipment,
  startDate,
  endDate,
}: PropsOtI): Promise<ServiceResponseI<DataI<OtI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    client && params.append("client", client.toString());
    supplier && params.append("supplier", supplier.toString());
    equipment && params.append("equipment", equipment.toString());

    startDate && params.append("startDate", startDate);
    endDate && params.append("endDate", endDate);

    //headers: { 'Authorization': validateToken
    const { data } = await otApi.get<ServiceResponseI<DataI<OtI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    throw new Error((err as Error).message);
  }
};

export const getAllOts = async (client?: number | null): Promise<ServiceResponseI<OtI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    client && params.append("client", client.toString());

    const { data } = await otApi.get<ServiceResponseI<OtI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken }, params }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((err as Error).message);
  }
};

export const otPost = async <T>(
  getForms: T
): Promise<ServiceResponseI<ObjPostI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.post<ServiceResponseI<ObjPostI>>(
      "",
      getForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const otPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const otById = async (id: number): Promise<ServiceResponseI<OtI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.get<ServiceResponseI<OtI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const otDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.delete<ServiceResponseI<string>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const otPdfbyId = async (id: number): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.get<ServiceResponseI<any>>(
      `/generate-pdf/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

// NUEVA FUNCIÓN PDF SIN AUTENTICACIÓN (USA TOKEN OTP)
export const otPdfByIdNoAuth = async (
  id: number, 
  otpToken: string
): Promise<ServiceResponseI<any>> => {
  try {
    const { data } = await otApi.get<ServiceResponseI<any>>(
      `/no-auth/generate-pdf/${id}`,
      { 
        headers: { 
          'X-OTP-Token': otpToken // Header especial para token OTP
        } 
      }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    throw new Error((obj?.data as any)?.message || (err as Error).message);
  }
};

// NUEVA FUNCIÓN SIN AUTENTICACIÓN (USA TOKEN OTP)
export const otByIdNoAuth = async (
  id: number, 
  otpToken: string
): Promise<ServiceResponseI<OtI>> => {
  try {
    const { data } = await otApi.get<ServiceResponseI<OtI>>(`/no-auth/${id}`, {
      headers: { 
        'X-OTP-Token': otpToken // Header especial para token OTP
      },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    throw new Error((obj?.data as any)?.message || (err as Error).message);
  }
};

export const getInformationContactByOtId = async (id: number): Promise<ServiceResponseI<{mail:string,phone:string}>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.get<ServiceResponseI<{mail:string, phone:string}>>(`/information-contact/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const getInformationSupplierContactByOtId = async (id: number): Promise<ServiceResponseI<{mail:string,phone:string}>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await otApi.get<ServiceResponseI<{mail:string, phone:string}>>(`/information-supplier-contact/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useOts = ({
  sorting,
  pagination,
  globalFilter,
  client,
  supplier,
  equipment,
  startDate,
  endDate,
}: PropsOtI) => {
  const otsQuery = useQuery(
    [
      "ots",
      {
        pagination,
        sorting,
        globalFilter,
        client,
        supplier,
        equipment,
        startDate,
        endDate,
      },
    ],
    () =>
      getOts({
        pagination,
        sorting,
        globalFilter,
        client,
        supplier,
        equipment,
        startDate,
        endDate,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      cacheTime: 0, 
      staleTime: 0
    }
  );

  return {
    otsQuery,
  };
};
